import { useEffect, useState } from "react";

// Keeps the chat container glued to the iOS visible viewport, glues the
// composer to the top of the keyboard, and locks body scroll while the
// room is mounted. All three effects were fused here to keep the parent
// component under 200 lines.
export function useRoomKeyboard({ containerRef, composerRef, scrollRef }) {
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  // Track VisualViewport → resize container + composer.
  useEffect(() => {
    if (typeof window === "undefined" || !window.visualViewport) return undefined;
    const vp = window.visualViewport;
    const apply = () => {
      const el = containerRef.current;
      if (el) {
        el.style.top = `${vp.offsetTop}px`;
        el.style.height = `${vp.height}px`;
      }
      // Glue composer to TOP OF KEYBOARD. Ignore vp.offsetTop — that spikes
      // during rubber-band scroll. window.innerHeight - vp.height is stable.
      const composer = composerRef.current;
      if (composer) {
        const kbInset = Math.max(0, window.innerHeight - vp.height);
        composer.style.bottom = `${kbInset}px`;
      }
      setKeyboardOpen((window.innerHeight - vp.height) > 120);
    };
    apply();
    vp.addEventListener("resize", apply);
    vp.addEventListener("scroll", apply);
    return () => {
      vp.removeEventListener("resize", apply);
      vp.removeEventListener("scroll", apply);
    };
  }, [containerRef, composerRef]);

  // Lock body scroll while the room is mounted.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const prev = {
      bodyOverflow: document.body.style.overflow,
      bodyBackground: document.body.style.background,
      bodyOverscroll: document.body.style.overscrollBehavior,
      htmlOverflow: document.documentElement.style.overflow,
      htmlBackground: document.documentElement.style.background,
    };
    document.body.style.overflow = "hidden";
    document.body.style.background = "#F4F4F2";
    document.body.style.overscrollBehavior = "none";
    document.documentElement.style.overflow = "hidden";
    document.documentElement.style.background = "#F4F4F2";
    return () => {
      document.body.style.overflow = prev.bodyOverflow;
      document.body.style.background = prev.bodyBackground;
      document.body.style.overscrollBehavior = prev.bodyOverscroll;
      document.documentElement.style.overflow = prev.htmlOverflow;
      document.documentElement.style.background = prev.htmlBackground;
    };
  }, []);

  // Pin to bottom whenever the visible viewport height changes.
  useEffect(() => {
    if (typeof window === "undefined" || !window.visualViewport) return undefined;
    const vp = window.visualViewport;
    const pinBottom = () => {
      requestAnimationFrame(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      });
    };
    vp.addEventListener("resize", pinBottom);
    return () => vp.removeEventListener("resize", pinBottom);
  }, [scrollRef]);

  return { keyboardOpen };
}
